import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, CircularProgress,
  Paper, Step, StepLabel, Stepper, Typography,
} from "@mui/material";
import ArrowBackIcon   from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon       from "@mui/icons-material/Check";
import { toast } from "react-toastify";
import api from "../../config/axios";

import PatientInfo   from "./sections/PatientInfo";
import PremorbidInfo from "./sections/PremorbidInfo";
import Diagnosis     from "./sections/Diagnosis";
import BaselineData  from "./sections/BaselineData";
import RgsConfig     from "./sections/RgsConfig";

const STEPS = [
  "Información del paciente",
  "Información premórbida",
  "Diagnóstico",
  "Datos basales",
  "Cuenta RGS+",
];

const INITIAL_FORM = {
  // Patient Information
  patient_code:    "",
  gender:          "",
  date_of_birth:   "",
  weight_kg:       "",
  education:       "",
  marital_status:  "",
  dominant_hand:   "",
  occupation:      "",

  // Premorbid Information
  vascular_risk_factors:          [],
  locomotor_problems:              null,
  previous_events:                 [],
  comorbidities:                   [],
  medical_resources:               "",
  physical_condition_days_week:    "",
  physical_condition_sport_types:  "",
  independence_status:             "",
  walking_status:                  "",
  falls_more_than_2:               null,
  falls_description:               "",

  // Diagnosis
  time_since_stroke_months: "",
  stroke_type:              "",
  stroke_location:          [],
  stroke_location_detail:   [],
  nihss:                    "",
  stroke_status:            "",
  affected_side:            "",
  neglect_present:          false,
  neglect_side:             "",
  visual_impairment_present: "",
  visual_impairment_type:   [],
  moca:                     "",
  depression_anxiety:       "",

  // Baseline Data
  nutritional_info:               "",
  autonomous_system:              "",
  grip_strength_affected_arm:     "",
  grip_strength_unaffected_arm:   "",
  grip_robot_measured:            null,
  arat:                           "",
  fugl_meyer_ue:                  "",

  // RGS+ Config
  PATIENT_USER:            "",
  PASSWORD:                "",
  EMAIL:                   "",
  PARETIC_SIDE:             "",
  UPPER_EXTREMITY_TO_TRAIN: "",
  HAND_RAISING_CAPACITY:    "",
};

export default function AddPatient() {
  const navigate = useNavigate();
  const [step,    setStep]    = useState(0);
  const [form,    setForm]    = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = { ...form };

      // Strings vacíos → null para campos numéricos
      const numericFields = [
        "weight_kg", "physical_condition_days_week", "independence_status",
        "time_since_stroke_months", "nihss", "moca",
        "grip_strength_affected_arm", "grip_strength_unaffected_arm",
        "arat", "fugl_meyer_ue",
      ];
      numericFields.forEach((f) => {
        if (payload[f] === "") payload[f] = null;
        else if (payload[f] !== null) payload[f] = Number(payload[f]);
      });

      // Strings vacíos → null para campos RGS+ opcionales
      ["PATIENT_USER", "PASSWORD", "EMAIL", "PARETIC_SIDE", "UPPER_EXTREMITY_TO_TRAIN", "HAND_RAISING_CAPACITY"].forEach((f) => {
        if (payload[f] === "") payload[f] = null;
      });

      await api.post("/patients", payload);
      toast.success("Paciente creado correctamente.");
      navigate("/patients");
    } catch (err) {
      const msg = err.response?.data?.error || "Error al guardar el paciente.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const sectionProps = { form, onChange: handleChange, onCheckboxChange: handleCheckboxChange };

  const sections = [
    <PatientInfo   {...sectionProps} />,
    <PremorbidInfo {...sectionProps} />,
    <Diagnosis     {...sectionProps} />,
    <BaselineData  {...sectionProps} />,
    <RgsConfig     {...sectionProps} />,
  ];

  return (
    <Box>
      {/* Cabecera */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/patients")}
          sx={{ minWidth: 0 }}
        >
          Volver
        </Button>
        <Typography variant="h5" fontWeight={700}>
          Nuevo paciente
        </Typography>
      </Box>

      {/* Stepper */}
      <Stepper activeStep={step} sx={{ mb: 4 }} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Sección actual */}
      <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 2, boxShadow: 2, mb: 3 }}>
        {sections[step]}
      </Paper>

      {/* Navegación entre pasos */}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          disabled={step === 0}
        >
          Anterior
        </Button>

        {step < STEPS.length - 1 ? (
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNext}
          >
            Siguiente
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
            onClick={handleSubmit}
            disabled={loading}
            sx={{ fontWeight: 700 }}
          >
            {loading ? "Guardando..." : "Guardar paciente"}
          </Button>
        )}
      </Box>
    </Box>
  );
}
